import { lazy as lazyMfe, SuspenseWithErrorBoundary } from '@signavio/mfe'
import {
  logout,
  prependOrg,
  types,
  useApi,
  useCallbackWhenRequestFails,
  useCallbackWhenRequestFulfills,
  useFeature,
  userUtils,
  SignavioProductT,
  useLoggedInSession,
} from '@signavio/effektif-api'
import { useMemo } from 'react'
import { useTheme } from '@signavio/ui'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import i18n, { locale as getLocale } from '@signavio/i18n'
import { getSearchUrl } from './utils'
import {
  Alert,
  applicationName,
  getUserGuideLang,
} from '@signavio/effektif-commons'
import { SWADetails } from './SWADetails'
import { useState } from 'react'
import { userGuideLink } from '@signavio/effektif-commons'
import { GlobalHeaderFallback } from './GlobalHeaderFallback'
import { UserMenuItems } from './UserMenuItems'
import { HelpMenuItems } from './HelpMenuItems'
import { PENDO_SWA_API_KEY } from './pendoAnalytics'
import { isDevEnvironment } from '../../utils/globalHeaderUtils'
import about from '../../about'
import { getBuiltInSupportConfig } from './builtInSupport'
import {
  HeaderPropsApi as GlobalHeaderProps,
  ApplicationAccessConfig,
} from 'global-mfe/types/header'

const HeaderModule = lazyMfe<GlobalHeaderProps>(
  () => import('GlobalHeader/Header')
)

const stringFilter = (value: unknown): value is string => Boolean(value)

export const GlobalHeaderLoggedIn = () => {
  const session = useLoggedInSession()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const [showAbout, setShowAbout] = useState(false)
  const [productsAccessConfig, setProductsAccessConfig] =
    useState<ApplicationAccessConfig>({})
  const { logo } = useTheme()

  const isPendoEnabled = useFeature('Component.PendoTrustArc')

  const isPXEnabled = !isDevEnvironment(window.location.hostname)

  const uxAnalyticsConfig = useMemo(() => {
    if (!isPendoEnabled) {
      return
    }

    return { analyticsAPIKey: PENDO_SWA_API_KEY, isBanner: true }
  }, [isPendoEnabled])

  const { user, organization: currentOrganization } = session

  const [logoutRequest, invalidateSession] = useApi(types.LOGOUT, {
    method: 'create',
  })

  const [enabledProductsRequest] = useApi(types.ENABLED_PRODUCTS, {
    method: 'fetch',
  })

  useCallbackWhenRequestFulfills(logoutRequest, () => dispatch(logout()))
  useCallbackWhenRequestFails(logoutRequest, () => dispatch(logout()))

  useCallbackWhenRequestFulfills(
    enabledProductsRequest,
    (enabledProductsList: SignavioProductT[]) => {
      let accessConfig: ApplicationAccessConfig = { SPG: true }

      enabledProductsList.forEach((enabledProduct) => {
        const productFeature = enabledProduct.key.toUpperCase()
        if (productFeature === 'SWA') {
          return
        }

        accessConfig[productFeature] = true
      })

      setProductsAccessConfig(accessConfig)
    }
  )

  const userGuideUrl = `${userGuideLink}/?locale=${getUserGuideLang()}`
  const locale = getLocale().toLowerCase()
  const globalHeaderHeight = window
    .getComputedStyle(document.body)
    .getPropertyValue('--global-header-height')

  const isBuiltInSupportEnabled = useFeature('Component.BuiltInSupport')

  const activeFeatureFlags: string[] = [
    'pendo-trustarc-manage-cookies',
    isBuiltInSupportEnabled && 'hub-built-in-support',
  ].filter(stringFilter)

  return (
    <SuspenseWithErrorBoundary fallback={<GlobalHeaderFallback />}>
      <div className={'headerContainer'}>
        <HeaderModule
          appCode={'processGovernance'}
          logoSrc={logo}
          onLogoClick={() => history.push(prependOrg('/cases/tasks'))}
          searchOptions={{
            onClickOverride: () =>
              history.push(getSearchUrl(location.pathname)),
          }}
          notificationOptions={{ visible: false }}
          disableAuthenticatedServices
          access={productsAccessConfig}
          createOptions={{
            visible: false,
          }}
          userMenuOptions={{
            onLogoutClick: () => invalidateSession(),
            onUserProfileClick: () => history.push(prependOrg('/profile')),
            additionalItems: <UserMenuItems />,
          }}
          userInfo={{
            id: user.id,
            name: userUtils.name(user),
            mail: user.emailAddress,
            company: currentOrganization.name || '',
            activeWorkspaceId: currentOrganization.id,
          }}
          helpMenuOptions={{
            userGuideLink: userGuideUrl,
            additionalItems: (
              <HelpMenuItems onAboutClick={() => setShowAbout(true)} />
            ),
          }}
          pxOptions={
            isPXEnabled
              ? {
                  visible: true,
                  appId: 'processGovernance',
                  appTitle: 'Process Governance',
                  context: {
                    appVersion: about.version,
                  },
                }
              : undefined
          }
          activeFeatureFlags={activeFeatureFlags}
          uxAnalytics={uxAnalyticsConfig}
          locale={locale}
          builtInSupportOptions={{
            visible: isBuiltInSupportEnabled,
            config: {
              ...getBuiltInSupportConfig({
                topOffset: globalHeaderHeight,
              }),
            },
          }}
        />
        {showAbout && (
          <Alert
            title={i18n('About __applicationName__', {
              applicationName,
            })}
            onDismiss={() => setShowAbout(false)}
          >
            <SWADetails />
          </Alert>
        )}
      </div>
    </SuspenseWithErrorBoundary>
  )
}
